import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TemplateMapperConfig, FilterOption, FilterValue, EmailTemplate, EntityFieldSource, FieldSourceKind } from '../base-template-mapper';
import { SampleBasedTemplateMapper, FILTER_KEYS } from '../sample-based-template-mapper';
import { SampleService } from '../../sample.service';
import { TemplateContext, ReportTemplate } from '../../../models/reportTemplate.model';
import { Sample } from '../../../models/sample.model';
import { IhLabResult } from '../../../models/IhLabResult.model';
import { UserService } from '../../user.service';
import { DateFormatService } from '../../dateFormat.service';
import { UserProfile } from '../../../models/userProfile.model';
import { markLenientSource } from '../../../shared/helpers/lenient-template-source';
import { ENTITY_TYPES } from 'src/app/shared/widget-registry';
import { PpeService } from '../../ppe.service';
import { Ppe } from '../../../models/ppe.model';
import { PpeTemplateData } from '../../../models/ppeTemplateData.model';
import { CustomFieldTypeEnum } from '../../../enums/customFieldType.enum';
import { CustomObjectService } from '../../customObject.service';
import { CustomField } from '../../../models/customField.model';
import { convertImageUrlToBase64 } from '../../../_helpers/image.helper';
import { ReportRiskLevelService } from '../report-risk-level.service';
import { LikelihoodColorLevel } from '../../../models/risk-assessment.model';
import { RiskLevel } from '../../../components/template-widgets/sample-widgets/gravimetric-widget/gravimetric-widget.component';
import { GravimetricPresentationService, GravimetricPresentationLabels } from '../../../components/template-widgets/sample-widgets/gravimetric-widget/gravimetric-presentation.service';
import { TranslateService } from '../../translate.service';
import { SampleTypeEnum } from '../../../enums/sampleType.enum';
import { SampleStatusEnum } from '../../../enums/sampleStatus.enum';
import { RatingTypeEnum } from '../../../enums/ratingTypeEnum.enum';
import { SampleCensorType } from '../../../enums/sampleCensorType.enum';

type ExceedanceEvaluation = 'exceeded' | 'not-exceeded' | 'not-comparable';

interface UserInputFieldConfig {
  default?: string | boolean;
  type?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SampleTemplateMapper extends SampleBasedTemplateMapper<Sample> {
  readonly config: TemplateMapperConfig = {
    entityType: ENTITY_TYPES.SAMPLE,
    supportedFilters: [FILTER_KEYS.agent, FILTER_KEYS.jurisdiction]
  };

  getEntityFieldSources(): EntityFieldSource[] {
    return [
      {
        key: 'sample',
        label: 'Sample',
        accent: 'var(--primary)',
        kind: FieldSourceKind.BeGrid,
        service: this.sampleService,
        pathPrefix: 'sample.',
        overlay: [
          { path: 'sample.oelWarningText', label: 'OEL Warning Text' },
          { path: 'sample.ppeWarningText', label: 'PPE Warning Text' },
          { path: 'sample.surveyName', label: 'Survey Name' },
          { path: 'sample.segName', label: 'SEG Name' },
          { path: 'sample.segCode', label: 'SEG Code' },
          { path: 'sample.organizationLevelName', label: 'Organization Level Name' },
        ],
      },
      {
        key: 'employee',
        label: 'Employee',
        accent: 'var(--info)',
        kind: FieldSourceKind.Inline,
        fields: [
          { path: 'employee.name', label: 'Employee Name' },
          { path: 'employee.employeeNumber', label: 'Employee Number' },
          { path: 'employee.employeeId', label: 'Employee ID' },
          { path: 'employee.email', label: 'Email' },
          { path: 'employee.department', label: 'Department' },
          { path: 'employee.location', label: 'Work Location' },
          { path: 'employee.jobTitle', label: 'Job Title' },
          { path: 'employee.firstName', label: 'First Name' },
          { path: 'employee.lastName', label: 'Last Name' },
          { path: 'employee.organizationLevelName', label: 'Organization Level' },
        ],
      },
      {
        key: 'organization',
        label: 'Organization',
        accent: 'var(--warning)',
        kind: FieldSourceKind.Inline,
        fields: [
          { path: 'organization.name', label: 'Organization Name' },
          { path: 'organization.companyName', label: 'Company Name' },
          { path: 'organization.location', label: 'Location' },
          { path: 'organization.tenantLogoImg', label: 'Company Logo', isImage: true },
        ],
      },
    ];
  }

  getEntityPreviewCss(scope: string): string {
    return `
${scope} figure.image:has(img.tenant-logo), ${scope} span.image-inline:has(img.tenant-logo) { display: inline-block; width: auto; max-width: 200px; margin: 0; }
${scope} img.tenant-logo { width: auto; max-width: 200px; max-height: 64px; object-fit: contain; }
`;
  }

  private userData?: UserProfile;
  private employeeCustomFields?: CustomField[];

  constructor(
    private sampleService: SampleService,
    private userService: UserService,
    dateFormatService: DateFormatService,
    private ppeService: PpeService,
    private customObjectService: CustomObjectService,
    private reportRiskLevelService: ReportRiskLevelService,
    private gravimetricPresentationService: GravimetricPresentationService,
    translateService: TranslateService
  ) {
    super(dateFormatService, translateService);
    this.loadEmployeeCustomFields();
  }

  protected getSamples(sample: Sample): Sample[] {
    return [sample];
  }

  async mapToTemplateContext(
    sample: Sample,
    template: ReportTemplate,
    userInputs: Record<string, string | boolean>,
    filteredData?: unknown[]
  ): Promise<TemplateContext> {
    if (!this.userData) {
      await this.loadAdditionalData(sample);
    }

    const labResults = (filteredData as IhLabResult[] | undefined) || sample.ihLabResults || [];
    const ppeAssignments = await this.loadPpeData(sample);
    const tenantRiskLevels = this.toGravimetricRiskLevels(await this.reportRiskLevelService.loadTenantRiskLevels());
    const jurisdictionLevels = await this.reportRiskLevelService.loadJurisdictionRiskLevels(
      labResults.map(r => r.jurisdictionId).filter((id): id is string => !!id)
    );
    const jurisdictionRiskLevelsMap = this.toGravimetricRiskLevelRecord(jurisdictionLevels);
    const riskLevels = tenantRiskLevels;

    const sampleCustomMappings = await this.buildSampleCustomMappings(sample, labResults, ppeAssignments);
    const employeeCustomMappings = this.buildEmployeeCustomMappings(sample);
    const organizationCustomMappings = await this.buildOrganizationCustomMappings(sample);

    const sampleData = this.createDynamicProxy(sample, sampleCustomMappings);
    const employeeData = this.createDynamicProxy(markLenientSource(sample.endPoint), employeeCustomMappings, true);
    const organizationData = this.createDynamicProxy(sample, organizationCustomMappings);

    const resolvedUserInputs = this.resolveUserInputDefaults(userInputs, template, sampleData, employeeData);

    const gravimetricPresentation = this.gravimetricPresentationService.build(
      { ...sample, ihLabResults: labResults },
      tenantRiskLevels,
      this.toRiskLevelMap(jurisdictionRiskLevelsMap),
      this.buildGravimetricLabels()
    );

    return {
      employee: employeeData,
      organization: organizationData,
      sample: sampleData,
      ppeAssignments,
      riskLevels,
      jurisdictionRiskLevelsMap,
      gravimetricData: { sample: sampleData },
      entities: { sample: sampleData, employee: employeeData, organization: organizationData },
      computedData: {
        gravimetricData: { sample: sampleData },
        gravimetricGroups: gravimetricPresentation.groups,
        gravimetricIsNoiseSample: gravimetricPresentation.isNoiseSample,
        gravimetricDuration: gravimetricPresentation.duration,
        gravimetricDurationLabel: gravimetricPresentation.durationLabel,
        gravimetricHasResults: gravimetricPresentation.hasResults
      },
      userInputs: resolvedUserInputs,
      ...this.buildSyntheticValues(this.userData, this.dateFormatService)
    };
  }

  private resolveUserInputDefaults(
    userInputs: Record<string, string | boolean>,
    template: ReportTemplate,
    sampleData: Record<string, unknown>,
    employeeData: Record<string, unknown>
  ): Record<string, string | boolean> {
    const resolved: Record<string, string | boolean> = { ...userInputs };
    const userInputsConfig = template?.templateJson?.declaredFields?.userInputs || {};

    for (const [key, config] of Object.entries(userInputsConfig)) {
      const fieldConfig = config as UserInputFieldConfig;

      if (resolved[key] !== undefined && resolved[key] !== null) continue;

      if (fieldConfig.default !== undefined && fieldConfig.default !== null) {
        let currentValue: string | boolean = fieldConfig.default;

        if (typeof currentValue === 'string' && currentValue.includes('[[') && currentValue.includes(']]')) {
          const placeholder = currentValue.match(/\[\[([^\]]+)\]\]/)?.[1];
          if (placeholder) {
            const [entity, ...pathParts] = placeholder.split('.');
            const path = pathParts.join('.');
            const entityData = entity === 'sample' ? sampleData : entity === 'employee' ? employeeData : null;
            if (entityData && path) {
              const resolvedValue = this.getNestedProperty(entityData, path);
              currentValue = resolvedValue == null ? '' : String(resolvedValue);
            } else {
              currentValue = '';
            }
          }
        }
        resolved[key] = currentValue;
      } else {
        resolved[key] = fieldConfig.type === 'checkbox' ? false : '';
      }
    }

    return resolved;
  }

  analyzeFilters(sample: Sample, template?: ReportTemplate): FilterOption[] {
    if (!sample?.ihLabResults?.length || !template?.templateJson?.supportedFilters?.length) return [];

    const filterMap = {
      [FILTER_KEYS.agent]: this.analyzeAgentFilter(sample.ihLabResults),
      [FILTER_KEYS.jurisdiction]: this.analyzeJurisdictionFilter(sample.ihLabResults)
    };

    return template.templateJson.supportedFilters
      .map(config => {
        const path = this.extractFilterPath(config.path);
        const data = filterMap[path];
        return data?.length ? {
          path,
          label: config.ui?.label || path,
          availableValues: data,
          selectedValues: data.map(d => d.value)
        } : null;
      })
      .filter(Boolean) as FilterOption[];
  }

  applyFilters(sample: Sample, filters: Record<string, (number | string)[]>): IhLabResult[] {
    if (!sample?.ihLabResults) return [];

    return sample.ihLabResults.filter(result => {
      const agentMatch = !filters[FILTER_KEYS.agent]?.length ||
        filters[FILTER_KEYS.agent].includes(result.agentId);
      const jurisdictionMatch = !filters[FILTER_KEYS.jurisdiction]?.length ||
        filters[FILTER_KEYS.jurisdiction].includes(result.jurisdictionId);
      return agentMatch && jurisdictionMatch;
    });
  }

  getDefaultUserInputs(): Record<string, string> {
    return {};
  }

  async loadAdditionalData(sample: Sample): Promise<void> {
    await Promise.all([
      !this.employeeCustomFields && this.loadEmployeeCustomFields(),
      !this.userData && this.loadUserProfile()
    ]);
  }

  private async loadUserProfile(): Promise<void> {
    this.userData = await firstValueFrom(this.userService.getCurrentUserProfile()).catch(() => new UserProfile());
  }

  private async loadEmployeeCustomFields(): Promise<void> {
    const customObjects = await firstValueFrom(this.customObjectService.getAll()).catch(() => []);
    const employeeType = customObjects?.find(t => t.name === 'Employee');
    if (employeeType) {
      this.employeeCustomFields = await firstValueFrom(
        this.customObjectService.getCustomObjectFields(employeeType.id)
      ).catch(() => []);
    }
  }

  private getPropertyByFieldType(endPoint: Record<string, unknown> | null | undefined, fieldType: CustomFieldTypeEnum): string {
    const field = this.employeeCustomFields?.find(f => f.type === fieldType);
    if (!field || !endPoint) return '';
    const value = endPoint[field.property];
    return typeof value === 'string' ? value : '';
  }

  private checkExceedance(ihLabResults: IhLabResult[] | undefined, checkFn: (result: IhLabResult, poel: number) => boolean): ExceedanceEvaluation {
    if (!ihLabResults?.length) return 'not-comparable';
    let hasComparableResult = false;
    for (const result of ihLabResults) {
      if (result.sampleCensor === SampleCensorType.LessThan) continue;
      const effectiveOel = this.getEffectiveOel(result);
      if (!effectiveOel || effectiveOel <= 0 || !Number.isFinite(effectiveOel)) continue;
      if (result.twa == null || !Number.isFinite(result.twa)) continue;
      // DELIBERATE: result.poel short-circuit was removed because the stored
      // poel value does not reflect the corrected (shift-adjusted) OEL. We always
      // recalculate as (twa / effectiveOel) * 100 so the exceedance check uses the
      // correct OEL basis. See DASH-1774 for context on the OEL correction feature.
      hasComparableResult = true;
      const poel = (result.twa / effectiveOel) * 100;
      if (checkFn(result, poel)) return 'exceeded';
    }
    return hasComparableResult ? 'not-exceeded' : 'not-comparable';
  }

  private getEffectiveOel(result: IhLabResult): number {
    // Use corrected OEL when shift adjustment is applied, otherwise use base OEL
    if (result.isOelCorrectionApplied && Number.isFinite(result.correctedOEL) && result.correctedOEL! > 0) {
      return result.correctedOEL!;
    }
    return Number.isFinite(result.oel) && result.oel! > 0 ? result.oel! : 0;
  }

  private checkPpeProtectionFactorExceedance(ihLabResults?: IhLabResult[], ppeAssignments?: PpeTemplateData[]): ExceedanceEvaluation {
    if (!ihLabResults?.length) return 'not-comparable';

    try {
      // Filter to respiratory PPE with Protection Factor rating
      const respiratoryPpe = (ppeAssignments || []).filter(
        ppe => ppe.ratingType === RatingTypeEnum.ProtectionFactor && ppe.rating > 0
      );

      // Default protection factor to 1 when no PPE assigned or no rating
      const defaultPf = 1;

      const maxRating = respiratoryPpe.length
        ? Math.max(...respiratoryPpe.map(ppe => ppe.rating ?? 0))
        : defaultPf;

      return this.checkExceedance(ihLabResults, (_, poel) =>
        (poel / 100) > maxRating
      );
    } catch (error) {
      return 'not-comparable';
    }
  }

  private checkNoisePpeExceedance(sample: Sample, ihLabResults?: IhLabResult[], ppeAssignments?: PpeTemplateData[]): ExceedanceEvaluation {
    // Only check for noise samples
    if (sample.sampleType !== SampleTypeEnum.Noise) return 'not-comparable';
    if (!ihLabResults?.length || !ppeAssignments?.length) return 'not-comparable';

    try {
      // Find noise PPE with NRR rating
      const noisePpe = ppeAssignments.find(
        ppe => ppe.ratingType === RatingTypeEnum.NRR && ppe.rating > 0
      );
      if (!noisePpe) return 'not-comparable';

      // NRR reduction formula: (NRR - 7) / 2
      const nrrReduction = Math.max((noisePpe.rating - 7) / 2, 0);

      // Check if any result exceeds OEL after NRR reduction
      let hasComparableResult = false;
      for (const result of ihLabResults) {
        const effectiveOel = this.getEffectiveOel(result);
        if (!effectiveOel || effectiveOel <= 0 || !Number.isFinite(effectiveOel)) continue;
        if (result.twa == null || !Number.isFinite(result.twa)) continue;
        hasComparableResult = true;
        const protectedTwa = result.twa - nrrReduction;
        if (protectedTwa > effectiveOel) return 'exceeded';
      }
      return hasComparableResult ? 'not-exceeded' : 'not-comparable';
    } catch (error) {
      return 'not-comparable';
    }
  }

  private async buildSampleCustomMappings(sample: Sample, ihLabResults?: IhLabResult[], ppeAssignments?: PpeTemplateData[]): Promise<Record<string, unknown>> {
    const dates = {
      startDate: this.formatDate(sample.startDate, 'N/A'),
      endDate: this.formatDate(sample.endDate, 'N/A'),
      dateCreated: this.formatDate(sample.dateCreated, 'N/A')
    };

    const isNoiseSample = sample.sampleType === SampleTypeEnum.Noise;
    const isVoid = sample.sampleStatus === SampleStatusEnum.Void;

    const oelEvaluation = this.checkExceedance(ihLabResults, (_, poel) => poel > 100);
    const hasOelExceedance = oelEvaluation === 'exceeded';
    const isOelComparable = oelEvaluation !== 'not-comparable';

    // Check PPE exceedance based on sample type
    const respiratoryPpeEvaluation = this.checkPpeProtectionFactorExceedance(ihLabResults, ppeAssignments);
    const hasRespiratoryPpeExceedance = respiratoryPpeEvaluation === 'exceeded';

    const noisePpeEvaluation = this.checkNoisePpeExceedance(sample, ihLabResults, ppeAssignments);
    const hasNoisePpeExceedance = noisePpeEvaluation === 'exceeded';

    // Use appropriate PPE exceedance based on sample type
    const hasPpeExceedance = isNoiseSample ? hasNoisePpeExceedance : hasRespiratoryPpeExceedance;
    const isPpeComparable = isNoiseSample
      ? noisePpeEvaluation !== 'not-comparable'
      : respiratoryPpeEvaluation !== 'not-comparable';

    // Get noise-specific PPE info
    const noisePpe = ppeAssignments?.find(ppe => ppe.ratingType === RatingTypeEnum.NRR && ppe.rating > 0);
    const nrrReduction = noisePpe ? Math.max((noisePpe.rating - 7) / 2, 0) : 0;

    // Check if respiratory PPE exists (for warning text)
    const hasRespiratoryPpe = (ppeAssignments || []).some(
      ppe => ppe.ratingType === RatingTypeEnum.ProtectionFactor && ppe.rating > 0
    );

    const labResultsWithLoq = (ihLabResults || sample.ihLabResults || []).map(lr => ({
      ...lr,
      isLoq: lr.sampleCensor === SampleCensorType.LessThan,
      loqDisplayText: lr.sampleCensor === SampleCensorType.LessThan
        ? this.translateService.getDynamicTranslation('< Limit of Quantification') : null
    }));
    const hasLoqResults = labResultsWithLoq.some(lr => lr.isLoq);
    // The no-exposure LOQ copy must only speak for the whole sample when EVERY lab result
    // is below the LOQ. If any result is above the LOQ, that detected result is the basis
    // for the statement (via the exceeded / within-limits copy driven by isOelComparable),
    // so a merely-partial <LOQ sample must not claim "no measurable exposure". See DASH-2546.
    const allResultsAreLoq = labResultsWithLoq.length > 0 && labResultsWithLoq.every(lr => lr.isLoq);

    return {
      ...dates,
      ...this.resolveSampleEnumLabels(sample),
      startDateFormatted: dates.startDate,
      endDateFormatted: dates.endDate,
      dateCreatedFormatted: dates.dateCreated,
      createdDate: dates.dateCreated,
      ihLabResults: labResultsWithLoq,
      hasLoqResults,
      hasOelExceedance,
      isOelComparable,
      hasPpeExceedance,
      isPpeComparable,
      // Sample type flags
      isNoiseSample,
      isAirSample: sample.sampleType === SampleTypeEnum.Air,
      isWipeSample: sample.sampleType === SampleTypeEnum.Wipe,
      // Void status
      isVoid,
      voidReason: sample.voidReason || '',
      // Noise-specific properties
      hasNoisePpeExceedance,
      isNoisePpeComparable: noisePpeEvaluation !== 'not-comparable',
      noisePpeNrr: noisePpe?.rating || 0,
      noisePpeName: noisePpe?.name || '',
      noiseNrrReduction: nrrReduction,
      // Warning texts
      // When a sample is non-comparable purely because every lab result is below the
      // laboratory's Limit of Quantification (<LOQ), that is a no-exposure outcome — not
      // missing/invalid data. checkExceedance() skips LessThan-censored results, so an
      // all-LOQ sample yields no comparable result and falls into the non-comparable branch.
      // Mixed samples (some analytes detected) have comparable results, so isOelComparable
      // is true and they correctly use the exceeded / within-limits copy below. The LOQ copy
      // also requires allResultsAreLoq so a non-comparable sample that still has an above-LOQ
      // (but un-evaluable) result does not falsely claim no measurable exposure. See DASH-2546.
      oelWarningText: !isOelComparable
        ? allResultsAreLoq
          ? this.translateService.getDynamicTranslation('Your sample result was below the laboratory\'s Limit of Quantification (LOQ). No measurable exposure was detected, and your exposure is within acceptable occupational limits. No further action is required.')
          : this.translateService.getDynamicTranslation('Exposure could not be evaluated against occupational exposure limits due to missing or invalid data.')
        : hasOelExceedance
          ? this.translateService.getDynamicTranslation('Your exposure for one or more agents exceeded the occupational exposure limit.')
          : this.translateService.getDynamicTranslation('Your exposure levels are within acceptable occupational limits.'),
      ppeWarningText: this.getPpeWarningText(isNoiseSample, hasPpeExceedance, isPpeComparable, noisePpe, nrrReduction, hasRespiratoryPpe),
      ppeWarningClass: !isPpeComparable ? 'warning-amber' : hasPpeExceedance ? 'warning-red' : 'warning-green'
    };
  }

  private getPpeWarningText(isNoiseSample: boolean, hasPpeExceedance: boolean, isPpeComparable: boolean, noisePpe: PpeTemplateData | undefined, nrrReduction: number, hasRespiratoryPpe: boolean = true): string {
    if (isNoiseSample) {
      if (!noisePpe) {
        return this.translateService.getDynamicTranslation('No hearing protection assigned for this noise sample.');
      }
      if (!isPpeComparable) {
        return this.translateService.getDynamicTranslation('PPE adequacy could not be evaluated due to missing or invalid exposure data.');
      }
      if (hasPpeExceedance) {
        return this.translateService.getDynamicTranslation(
          'Hearing protection (NRR {{nrr}}, {{reduction}} dB reduction) does not provide adequate protection. TWA exceeds OEL after NRR reduction.',
          { nrr: noisePpe.rating, reduction: nrrReduction.toFixed(1) }
        );
      }
      return this.translateService.getDynamicTranslation(
        'Hearing protection (NRR {{nrr}}, {{reduction}} dB reduction) provides adequate protection.',
        { nrr: noisePpe.rating, reduction: nrrReduction.toFixed(1) }
      );
    }
    if (!isPpeComparable) {
      return this.translateService.getDynamicTranslation('PPE adequacy could not be evaluated due to missing or invalid exposure data.');
    }
    // Respiratory PPE (default PF=1 when no PPE assigned)
    if (hasPpeExceedance) {
      return hasRespiratoryPpe
        ? this.translateService.getDynamicTranslation('The maximum use concentration of the PPE was exceeded.')
        : this.translateService.getDynamicTranslation('No respiratory protection assigned. Exposure exceeds default protection factor of 1.');
    }
    return hasRespiratoryPpe
      ? this.translateService.getDynamicTranslation('The maximum use concentration of the PPE was not exceeded for any agent.')
      : this.translateService.getDynamicTranslation('No respiratory protection assigned. Exposure is within default protection factor of 1.');
  }

  private buildEmployeeCustomMappings(sample: Sample): Record<string, unknown> {
    const endPoint = sample.endPoint;
    if (!endPoint) {
      return {
        id: null, name: 'N/A', employeeNumber: 'N/A', employeeId: 'N/A',
        email: 'N/A', department: 'N/A', location: 'N/A',
        jobTitle: sample.segName || 'N/A', firstName: 'N/A', lastName: 'N/A',
        organizationLevelName: sample.organizationLevelName || 'N/A'
      };
    }

    const getField = (type: CustomFieldTypeEnum) => this.getPropertyByFieldType(endPoint, type);
    const firstName = getField(CustomFieldTypeEnum.HrisFirstName);
    const lastName = getField(CustomFieldTypeEnum.HrisLastName);
    const employeeId = getField(CustomFieldTypeEnum.HrisPersonId);
    const fullName = `${firstName} ${lastName}`.trim() || endPoint.name || 'N/A';

    return {
      name: fullName,
      employeeNumber: employeeId || 'N/A',
      employeeId: employeeId || 'N/A',
      email: getField(CustomFieldTypeEnum.HrisEmailId) || 'N/A',
      department: getField(CustomFieldTypeEnum.HrisJobName) || 'N/A',
      location: getField(CustomFieldTypeEnum.HrisWorkLocation) || 'N/A',
      jobTitle: getField(CustomFieldTypeEnum.HrisPosition) || sample.segName || 'N/A',
      firstName: firstName || 'N/A',
      lastName: lastName || 'N/A',
      organizationLevelName: endPoint.organizationLevelName || sample.organizationLevelName || 'N/A'
    };
  }

  private async buildOrganizationCustomMappings(sample?: Sample): Promise<Record<string, unknown>> {
    const workLocation = sample?.endPoint ? this.getPropertyByFieldType(sample.endPoint, CustomFieldTypeEnum.HrisWorkLocation) : '';
    const companyName = this.userData?.companyName || 'N/A';

    let tenantLogoImg = '';
    try {
      const logoUrl = await firstValueFrom(this.userService.getTenantLogoUrl());
      if (logoUrl) {
        tenantLogoImg = await convertImageUrlToBase64(logoUrl);
      }
    } catch (error) {
    }

    return {
      name: companyName,
      companyName,
      location: workLocation || sample?.organizationLevelName || this.userData?.defaultMapLocation || 'N/A',
      tenantLogoImg
    };
  }

  private analyzeAgentFilter(ihLabResults: IhLabResult[]): FilterValue[] {
    return this.buildFilterValues(ihLabResults, r => r.agentId, r => r.agentName || `Agent ${r.agentId}`);
  }

  private analyzeJurisdictionFilter(ihLabResults: IhLabResult[]): FilterValue[] {
    return this.buildFilterValues(ihLabResults, r => r.jurisdictionId, r => r.jurisdictionName || `Jurisdiction ${r.jurisdictionId}`);
  }

  private async loadPpeData(sample: Sample): Promise<PpeTemplateData[]> {
    if (!sample.ppeAssignments?.length) return [];

    const uniquePpeIds = [...new Set(sample.ppeAssignments.map(a => a.ppeId))];
    const ppes = await firstValueFrom(this.ppeService.getByIds(uniquePpeIds)).catch((): Ppe[] => []);
    const ppeById = new Map(ppes.map(ppe => [ppe.id, ppe]));
    const ppeDetails = uniquePpeIds
      .map(id => ppeById.get(id))
      .filter((ppe): ppe is Ppe => !!ppe);

    const iconMap: Record<number, string> = { 0: 'assets/images/mask.svg', 1: 'assets/images/hearing.svg' };
    const ratingTypeMap: Record<number, string> = { 1: 'NRR', 2: 'Protection Factor' };

    // GetList fills iconUrl with a signed storage URL that Get never returned; the report keeps the bundled icons (multi-sample widgets detect respirators by 'mask.svg').
    return ppeDetails.map(ppe => ({
      iconUrl: iconMap[ppe.type] || '',
      name: ppe.name || '',
      modelName: ppe.model || ppe.name?.split(' APF=')[0] || '',
      apf: ppe.name?.includes('APF=') ? ppe.name.split('APF=')[1] : '0',
      brand: ppe.brand || '',
      rating: ppe.rating || 0,
      ratingType: ppe.ratingType || null,
      ratingTypeName: ppe.ratingType ? (ratingTypeMap[ppe.ratingType] || 'Unknown') : ''
    }));
  }

  getEmailTemplate(sample: Sample, customFields?: CustomField[]): EmailTemplate {
    const endPoint = sample?.endPoint;
    const employeeName = this.getEmployeeName(sample, customFields);
    const sampleDate = sample?.startDate
      ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(String(sample.startDate), this.dateFormatService.dateStartingWithMonthFormat)
      : '[Sample Date]';
    const managingHygienistName = sample?.managingHygienistFullName || '[Managing Hygienist]';
    const managingHygienistEmail = sample?.managingHygienistUserName || '';

    const recipientEmail = endPoint ? this.getFieldValue(endPoint, CustomFieldTypeEnum.HrisEmailId, customFields) : '';
    const recipientId = endPoint?.id;
    const ccEmails = this.getSampleCcEmails(sample, customFields);

    return {
      subject: 'Exposure monitoring personal letter',
      message: `FROM: ${managingHygienistEmail}\n\nDear ${employeeName},\n\nPlease find attached your exposure monitoring results for sampling conducted on ${sampleDate}.\n\nRegards,\n${managingHygienistName}`,
      recipientEmail,
      recipientId,
      ccEmails
    };
  }

  private getEmployeeName(sample: Sample, customFields?: CustomField[]): string {
    const endPoint = sample?.endPoint;
    if (!endPoint) return '[Employee Name]';

    const firstName = this.getFieldValue(endPoint, CustomFieldTypeEnum.HrisFirstName, customFields);
    const lastName = this.getFieldValue(endPoint, CustomFieldTypeEnum.HrisLastName, customFields);
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || endPoint.name || '[Employee Name]';
  }

  private getSampleCcEmails(sample: Sample, customFields?: CustomField[]): string[] {
    const ccEmails: string[] = [];

    // Only include managing hygienist in CC, not supervisor
    const hygienistEmail = sample?.managingHygienistUserName;
    if (hygienistEmail && this.isValidEmail(hygienistEmail)) {
      ccEmails.push(hygienistEmail);
    }

    return ccEmails;
  }

  private getNestedProperty(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce<unknown>((current, prop) => {
      if (current && typeof current === 'object' && prop in current) {
        return (current as Record<string, unknown>)[prop];
      }
      return undefined;
    }, obj);
  }

  private toRiskLevelMap(record: Record<string, RiskLevel[]>): Map<string, RiskLevel[]> {
    const map = new Map<string, RiskLevel[]>();
    Object.entries(record).forEach(([key, levels]) => map.set(key, levels));
    return map;
  }

  /**
   * Adapts the shared parser's `LikelihoodColorLevel` (min/maxQuantThreshold) to the gravimetric
   * `RiskLevel` (`QuantThreshold_OEL` tuple) the gravimetric widget and presentation service consume.
   */
  private toGravimetricRiskLevels(levels: LikelihoodColorLevel[]): RiskLevel[] {
    return levels.map(level => ({
      Name: level.Name ?? '',
      QuantThreshold_OEL: [Number(level.minQuantThreshold), Number(level.maxQuantThreshold)] as [number, number],
      MinSamples: level.MinSamples ?? 0,
      Color: level.Color ?? ''
    }));
  }

  private toGravimetricRiskLevelRecord(record: Record<string, LikelihoodColorLevel[]>): Record<string, RiskLevel[]> {
    const result: Record<string, RiskLevel[]> = {};
    Object.entries(record).forEach(([key, levels]) => { result[key] = this.toGravimetricRiskLevels(levels); });
    return result;
  }

  private buildGravimetricLabels(): GravimetricPresentationLabels {
    return {
      duration: this.translateService.getDynamicTranslation('Duration'),
      analyte: this.translateService.getDynamicTranslation('Analyte'),
      dosePercent: this.translateService.getDynamicTranslation('Dose %'),
      shiftAdjustedDosePercent: this.translateService.getDynamicTranslation('Shift Adjusted Dose %'),
      percentageOfOel: this.translateService.getDynamicTranslation('Percentage of OEL'),
      dbaTwa: this.translateService.getDynamicTranslation('dBA (TWA)'),
      jurisdiction: this.translateService.getDynamicTranslation('Jurisdiction'),
      oel: this.translateService.getDynamicTranslation('OEL'),
      dba: this.translateService.getDynamicTranslation('dBA'),
      riskLegendDose: this.translateService.getDynamicTranslation('Risk Legend as % of Dose:'),
      riskLegendOel: this.translateService.getDynamicTranslation('Risk Legend as % of Occupational Exposure Limit (OEL):'),
      loqDisplay: this.translateService.getDynamicTranslation('< Limit of Quantification'),
      shiftAdjustedLabel: this.translateService.getDynamicTranslation('(Shift Adjusted)')
    };
  }
}
